<script setup lang="ts">
import { computed, useSlots } from 'vue';

type EmptyStateVariant = 'page' | 'card' | 'compact' | 'table';

const props = withDefaults(defineProps<{
    variant?: EmptyStateVariant;
    icon?: string;
    title: string;
    description?: string;
    hint?: string;
}>(), {
    variant: 'card',
    icon: 'inbox',
    description: '',
    hint: ''
});

const slots = useSlots();
const { $t } = useLocalization();

const variantClasses: Record<EmptyStateVariant, string> = {
    page: 'min-h-[320px] px-[24px] py-[48px]',
    card: 'min-h-[192px] rounded-[8px] border border-dashed border-border px-[20px] py-[38px] dark:border-border-dark',
    compact: 'min-h-[96px] px-[16px] py-[20px]',
    table: 'h-full min-h-[128px] flex-1 px-[16px] py-[24px]'
};

const iconClasses: Record<EmptyStateVariant, string> = {
    page: 'size-[40px]',
    card: 'size-[40px]',
    compact: 'size-[40px]',
    table: 'size-[40px]'
};

const classes = computed(() => [
    'flex flex-col items-center justify-center gap-[10px] text-center',
    variantClasses[props.variant]
]);
</script>

<template>
    <div :class="classes">
        <span :class="['inline-flex shrink-0 items-center justify-center rounded-full bg-surface-3 text-fg-muted dark:bg-surface-3-dark dark:text-fg-muted-dark', iconClasses[props.variant]]">
            <IdeoIcon :name="props.icon" :size="19" />
        </span>
        <IdeoHeading tag="h3" class="m-0 text-ui-body/[1.4] font-[600] text-fg dark:text-fg-dark">
            {{ $t(props.title) }}
        </IdeoHeading>
        <p v-if="props.description" class="m-0 max-w-[320px] text-ui-body/[1.5] font-[400] text-fg-muted dark:text-fg-muted-dark">
            {{ $t(props.description) }}
        </p>
        <p v-if="props.hint" class="m-0 max-w-[320px] text-[0.71875rem]/[1.45] font-[400] text-fg-faint dark:text-fg-faint-dark">
            {{ $t(props.hint) }}
        </p>
        <IdeoActionGroup v-if="slots.action" class="mt-[4px]">
            <slot name="action" />
        </IdeoActionGroup>
    </div>
</template>
